import React, { useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { Lock, KeyRound, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function ResetPassword() {
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const navigate = useNavigate();
    const [pwd, setPwd] = useState("");
    const [confirm, setConfirm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink px-6 grain pt-24" data-testid="reset-no-token">
                <div className="max-w-md w-full bg-white/5 backdrop-blur-md border border-rose-500/30 rounded-sm p-8 text-center">
                    <AlertTriangle size={32} className="mx-auto text-rose-400 mb-4" />
                    <h2 className="font-serif text-2xl text-white mb-2">Link non valido</h2>
                    <p className="text-gray-400 text-sm">Il link che hai utilizzato non contiene un token. Richiedi un nuovo link di reset.</p>
                    <Link to="/admin/forgot-password" className="inline-block mt-5 text-gold hover:text-gold-light text-xs tracking-widest uppercase">
                        Richiedi nuovo link
                    </Link>
                </div>
            </div>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        if (pwd !== confirm) {
            toast.error("Le password non coincidono");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/admin/reset-password", { token, new_password: pwd });
            setDone(true);
            toast.success("Password aggiornata!");
            setTimeout(() => navigate("/admin/login"), 1800);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : "Errore durante il reset");
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-ink px-6 grain pt-24" data-testid="reset-done">
                <div className="max-w-md w-full bg-white/5 backdrop-blur-md border border-emerald-500/30 rounded-sm p-8 text-center">
                    <CheckCircle2 size={36} className="mx-auto text-emerald-300 mb-4" />
                    <h2 className="font-serif text-2xl text-white">Password aggiornata</h2>
                    <p className="text-gray-400 text-sm mt-2">Reindirizzamento al login...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-ink px-6 grain pt-24" data-testid="reset-password-page">
            <form onSubmit={submit} className="max-w-md w-full bg-white/5 backdrop-blur-md border border-white/10 rounded-sm p-8">
                <div className="text-center mb-6">
                    <KeyRound size={28} className="mx-auto text-gold mb-3" strokeWidth={1.5} />
                    <h2 className="font-serif text-3xl text-white">Nuova password</h2>
                    <p className="text-gray-400 text-xs mt-2">Scegli una password sicura di almeno 8 caratteri.</p>
                </div>

                <div className="space-y-4">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">Nuova password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />
                            <input type="password" required minLength={8} maxLength={80}
                                value={pwd} onChange={(e) => setPwd(e.target.value)}
                                data-testid="reset-password"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-1.5">Conferma password</label>
                        <div className="relative">
                            <Lock className="absolute left-3 top-3 text-gold/70" size={16} strokeWidth={1.5} />
                            <input type="password" required minLength={8} maxLength={80}
                                value={confirm} onChange={(e) => setConfirm(e.target.value)}
                                data-testid="reset-password-confirm"
                                className="w-full pl-10 pr-3 py-3 bg-transparent border border-white/15 rounded-sm text-white focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold text-sm"
                            />
                        </div>
                    </div>

                    <button type="submit" disabled={submitting}
                        data-testid="reset-submit"
                        className="w-full bg-gold text-black font-semibold py-3.5 rounded-sm hover:bg-gold-light transition-colors tracking-widest uppercase text-sm disabled:opacity-40">
                        {submitting ? "Aggiornamento..." : "Aggiorna password"}
                    </button>
                </div>
            </form>
        </div>
    );
}
